import {
    AfterViewChecked,
    Component,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Signal,
    signal,
    SimpleChanges,
    viewChild,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { PanZoomComponent, PanZoomModel } from 'ngx-panzoom';
import { filter, Subject, Subscription, switchMap, tap } from 'rxjs';
import { ImgWithSelection } from 'src/app/img-db/model/img-with-selection';
import { ImgAnnotation } from '../model/img-annotation';
import { ImgCategory } from '../model/img-category';
import { ImgDbZoomService } from '../services/img-db-zoom.service';
import { AnnotationStateService } from '../services/annotation-state.service';
import { ColorService } from '../../shared/services/color.service';
import { ImagesStorageService } from '../services/images-storage.service';
import { take, takeUntil } from 'rxjs/operators';
import { PagingResult } from '../../shared/models/pageable';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { SplitScreenState } from '../../project-split-screen/components/project-split-screen/project-split-screen.component';
import { KeyEventsService } from '../services/key-events.service';
import { AnnotationClipboardData } from '../model/annotation-clipboard-data';
import { ToastMessageService } from '../../shared/services/toast-message.service';

@Component({
    selector: 'app-img-labeling-viewer',
    templateUrl: './img-labeling-viewer.component.html',
    styleUrls: ['./img-labeling-viewer.component.scss'],
    standalone: false,
})
export class ImgLabelingViewerComponent
    implements OnInit, OnChanges, AfterViewChecked, OnDestroy
{
    // panZoom configuration object
    panZoomConfig = {
        zoomLevels: ImgDbZoomService.MAX_IMAGE_ZOOM, // default + 20 levels
        scalePerZoomLevel: 1.2, // 20% zoom per level
        neutralZoomLevel: 10,
        zoomOnMouseWheel: true,
        zoomOnDoubleClick: false,
        invertMouseWheel: true,
        freeMouseWheel: false, // make mouse wheel zoom in 20% steps as defined above
        panOnClickDrag: false,
        zoomToFitZoomLevelFactor: 0.98,
        dragMouseButton: 'left',
    };

    @Output()
    zoomLevelChanged: EventEmitter<number> = new EventEmitter();

    @Input() selectedCategory: ImgCategory;
    @Input() categories: ImgCategory[];
    @Input() splitScreenState: SplitScreenState;
    @Input() canEdit: boolean;
    isDraggingActive: boolean = false;

    selectedImage: ImgWithSelection;
    timer: any;
    imageLoaded: boolean = false;
    annotationsLoading: boolean = false;
    alreadyDestroyed: boolean = false;
    cornerSize: number = 6; // size of the corners
    private cornerClasses = [
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
    ];
    private resizeObserver: ResizeObserver;

    private _labeling: ElementRef;
    /**
     * The `<svg #labeling>` element is wrapped in `*ngIf="isImageAndAnnotationsReady"`.
     * On every image switch the SVG is destroyed (loading state) and recreated
     * as a fresh DOM node once the new image+annotations are ready. The
     * drawing `mousedown` listener attached via {@link enableDrawing} is bound
     * to a specific DOM element, so it is gone with the old SVG. Reattaching
     * happens here in the ViewChild setter, which fires whenever the query
     * result changes (mount, unmount, remount). Without this, drawing silently
     * stops working after image navigation - the only ngOnChanges branch that
     * reattaches requires `selectedCategory` to change, which is why
     * deselecting and re-selecting the category was the only workaround.
     */
    @ViewChild('labeling') set labeling(value: ElementRef) {
        if (value === this._labeling) {
            return;
        }
        // The previous SVG (if any) was torn down by *ngIf and the listener
        // it carried was garbage-collected with it. Drop the dangling
        // reference so enableDrawing() will attach a fresh one to the new
        // element rather than treating the listener as still active.
        this.drawingEventListenerRef = null;
        this._labeling = value;
        if (value?.nativeElement) {
            // enableDrawing() guards on isEditable() internally, so it is a
            // no-op when there is no selected category or canEdit is false.
            this.enableDrawing(true);
        }
    }
    get labeling(): ElementRef {
        return this._labeling;
    }
    @ViewChild('currentImage') currentImage: ElementRef;
    oldImageSrc: String;
    panzoomParentSignal: Signal<ElementRef> = viewChild('panzoomParent'); //temporary duplication signal and element
    panzoomParent: ElementRef;
    panZoomComponentSignal: Signal<PanZoomComponent> =
        viewChild(PanZoomComponent); //temporary duplication signal and element
    panZoomComponent: PanZoomComponent;

    @ViewChildren('img') queryList: QueryList<ElementRef>;

    zoomConfigPanOnClickDrag = signal<boolean>(
        this.panZoomConfig.panOnClickDrag
    );
    serializedImgRefs: Element[] = [];
    svgStatus: any;
    annotations: ImgAnnotation[];
    initializeAnnotations: boolean = false;
    drawingEventListenerRef: any;
    keepInBoundsTranslocationInitialLimit: number = 100000;
    keepInBoundsTranslocationLimit: number = this.keepInBoundsTranslocationInitialLimit;

    wantedZoomLevel: number = null;

    // check if panning was started and ended
    imagePanned: boolean = true;

    /**used to set and clear delayed alignment after zoom*/
    zoomAlignmentTimeoutId: any;

    /**timeout for pan delta handling*/
    panDeltaTimeout: any;

    // used to set and clear saving Interval
    saveIntervalId: any;

    cursorView: string = 'default';

    private subscriptions: Subscription[] = [];
    private destroy$: Subject<void> = new Subject<void>();
    private isMouseActionActive: boolean = false;
    private spaceActionTriggered: 'on' | 'off' | null = null;

    showImageContainer: boolean = false;

    isPanButtonActive: boolean = false;
    isPanButtonClicked: boolean = this.isPanButtonActive;
    isRulerButtonActive: boolean = false;
    showSizeLabels: boolean = this.isRulerButtonActive;
    sizeLabelCache: object = {}; // a lot of computation if a lot of annotations, so let's cache it
    initializedSizeLabels: {} = {};
    activateSpaceLambda = (event) => this.onActivateSpaceDragging(event);
    deactivateSpaceLambda = (event) => this.onDeactivateSpaceDragging(event);
    handlePostponedActionLambda = () => this.handlePostponedAction();
    keydownUndoRedoLambda = (event: KeyboardEvent) =>
        this.handleKeydownUndoRedoEvents(event);
    keydownBLambda = (event: KeyboardEvent) => this.handleKeydownBEvent(event);
    keyupBLambda = (event: KeyboardEvent) => this.handleKeyupBEvent(event);

    keydownUndoRedoEventsExist: boolean = false;
    keydownAndUpBEventsExist: boolean = false;
    keydownBAccepted: boolean = false;

    ctrlCLambda = (event: KeyboardEvent) => this.handleCtrlCEvent(event);
    ctrlCEventExists: boolean = false;

    ctrlVLambda = (event: KeyboardEvent) => this.handleCtrlVEvent(event);
    ctrlVEventExists: boolean = false;

    sLambda = (event: KeyboardEvent) => this.handleSEvent(event);
    sEventExists: boolean = false;

    featureButtonActiveIdx: number | null = null;
    readonly moveButtonHeight = 32;
    readonly boundingBoxTitleHeight = 20;

    readonly TOAST_MESSAGE_DELAY = { delay: 2000 };

    constructor(
        public annotationStateService: AnnotationStateService,
        private colorService: ColorService,
        private zoomService: ImgDbZoomService,
        private storage: ImagesStorageService,
        private authService: AuthenticationService,
        private keyEventsService: KeyEventsService,
        private messageService: ToastMessageService
    ) {
        effect(() => {
            if (this.panzoomParentSignal()) {
                this.panzoomParent = this.panzoomParentSignal();
                this.resizeObserver = new ResizeObserver(
                    this.onResize.bind(this)
                );
                this.resizeObserver.observe(this.panzoomParent.nativeElement);
            }
        });

        effect(() => {
            if (this.panZoomComponentSignal()) {
                this.panZoomComponent = this.panZoomComponentSignal();
                this.panZoomComponent['isMobile'] = false; //dirty fix for MouseWheelzoom when Device with touchscreen
            }
        });
    }

    get currentZoomLevel() {
        return this.zoomService.getCurrentZoomLevel(1);
    }

    ngOnInit() {
        this.subscriptions.push(
            this.annotationStateService.$loading.subscribe((isLoading: boolean) => {
                this.annotationsLoading = isLoading;
            })
        );

        this.subscriptions.push(
            this.storage.imagesPage$
                .pipe(
                    filter(
                        (page: PagingResult<ImgWithSelection>) => page !== null
                    ),
                    take(1),
                    tap((_) => this.setSvg()),
                    switchMap(() => {
                        return this.annotationStateService.$annotations.pipe(
                            tap((annotations: ImgAnnotation[]) => {
                                this.annotations = annotations;
                                // reset cache if an image loads so we don't store unnecessary data
                                if (
                                    this.currentImage &&
                                    (this.oldImageSrc === null ||
                                        this.oldImageSrc !==
                                            this.currentImage.nativeElement
                                                .currentSrc)
                                ) {
                                    this.resetSizeLabelCache();
                                    this.oldImageSrc =
                                        this.currentImage.nativeElement.currentSrc;
                                }
                                this.initializeAnnotations = true; // 1. display labels in backbuffer so we get the DOM elements for calculations and nothing flickers
                                this.updatePositions();
                                requestAnimationFrame(() => {
                                    // 2. request labels to be rendered, so we can correctly calculate text width
                                    this.annotations.forEach(
                                        (annotation: ImgAnnotation) => {
                                            // calculate only  annotations which have not been initialized yet
                                            if (
                                                this.sizeLabelsNeedUpdate(
                                                    annotation
                                                )
                                            ) {
                                                this.updateSizeLabels(
                                                    annotation
                                                ); // 3.  calculate label sizes
                                                this.initializedSizeLabels[
                                                    annotation.id
                                                ] = { ...annotation.bbox };
                                            }
                                        }
                                    );

                                    requestAnimationFrame(() => {
                                        // 3. disable backbuffer
                                        this.initializeAnnotations = false;
                                    });
                                });
                            }),
                            takeUntil(this.destroy$)
                        );
                    }),
                    switchMap(() => {
                        return this.zoomService.zoomLevelLabelDialog.pipe(
                            tap((zoomLevel: number) => {
                                this.onZoomChanged(zoomLevel);
                            }),
                            takeUntil(this.destroy$)
                        );
                    })
                )
                .subscribe()
        );
        this.subscriptions.push(
            this.authService.logoutTriggered$.subscribe(() => {
                this.annotationStateService
                    .saveCommandsBeforeLogout()
                    .subscribe(() => {
                        this.authService.logoutPreparationsFinished$.next(true);
                    });
            })
        );
        this.subscriptions.push(
            this.storage.selectedImage$.subscribe((img: ImgWithSelection) => {
                if (img) {
                    if (
                        this.oldImageSrc &&
                        this.oldImageSrc !== img.processedUrl
                    ) {
                        this.imageLoaded = false;
                    }
                    clearInterval(this.saveIntervalId);
                    this.selectedImage = img;
                    this.showImageContainer = true;
                    this.saveIntervalId = setInterval(() => {
                        this.annotationStateService
                            .saveCommands(true, img.id)
                            .subscribe();
                    }, 20000);

                    this.onImagesChanged();
                }
            })
        );

        this.subscriptions.push(
            this.storage.pageChanged$.subscribe(
                () => (this.showImageContainer = false)
            )
        );

        document.body.addEventListener('keydown', this.activateSpaceLambda);
        document.body.addEventListener('keyup', this.deactivateSpaceLambda);
        document.body.addEventListener(
            'mouseleave',
            this.handlePostponedActionLambda
        );
        document.body.addEventListener(
            'mouseup',
            this.handlePostponedActionLambda
        );
        this.addCtrlCEvent();
        this.addSEvent();

        this.subscriptions.push(
            this.keyEventsService.$modalOpen.subscribe((isOpen: boolean) => {
                if (isOpen) {
                    this.removeKeydownUndoRedoEvents();
                    this.removeKeydownAndUpBEvents();
                    this.removeCtrlCEvent();
                    this.removeCtrlVEvent();
                    this.removeSEvent();
                } else {
                    this.addKeydownUndoRedoEvents();
                    this.addKeydownAndUpBEvents();
                    this.addCtrlCEvent();
                    this.addCtrlVEvent();
                    this.addSEvent();
                }
            })
        );
    }

    private addCtrlCEvent() {
        if (!this.ctrlCEventExists) {
            document.addEventListener('keydown', this.ctrlCLambda);
            this.ctrlCEventExists = true;
        }
    }

    private removeCtrlCEvent() {
        if (this.ctrlCEventExists) {
            document.removeEventListener('keydown', this.ctrlCLambda);
            this.ctrlCEventExists = false;
        }
    }

    private addCtrlVEvent() {
        if (!this.ctrlVEventExists && this.canEdit) {
            document.addEventListener('keydown', this.ctrlVLambda);
            this.ctrlVEventExists = true;
        }
    }

    private removeCtrlVEvent() {
        if (this.ctrlVEventExists) {
            document.removeEventListener('keydown', this.ctrlVLambda);
            this.ctrlVEventExists = false;
        }
    }

    private addSEvent() {
        if (!this.sEventExists) {
            document.addEventListener('keydown', this.sLambda);
            this.sEventExists = true;
        }
    }

    private removeSEvent() {
        if (this.sEventExists) {
            document.removeEventListener('keydown', this.sLambda);
            this.sEventExists = false;
        }
    }

    private async handleSEvent(event: KeyboardEvent) {
        if (event.key === 's' || event.key === 'S') {
            this.onRulerButtonClick();
        }
    }

    private async handleCtrlVEvent(event: KeyboardEvent) {
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'v' || event.key === 'V')
        ) {
            event.stopPropagation();

            await this.keyEventsService
                .readAnnotationFromClipboard()
                .then((annotationClipboardData: AnnotationClipboardData) => {
                    if (!annotationClipboardData) {
                        this.messageService.showTranslatedWarningMsg(
                            'imgDb.imgLabeling.pasteAnnotationEvent.invalidAnnotation',
                            null,
                            this.TOAST_MESSAGE_DELAY
                        );

                        return;
                    }

                    const categoryOfAnnotationFromClipboard =
                        this.categories.find(
                            (category) =>
                                category.name ===
                                annotationClipboardData.categoryName
                        );

                    if (!categoryOfAnnotationFromClipboard) {
                        this.messageService.showTranslatedErrorMsg(
                            'imgDb.imgLabeling.pasteAnnotationEvent.notExistingCategory',
                            {
                                categoryName:
                                    annotationClipboardData.categoryName,
                            },
                            this.TOAST_MESSAGE_DELAY
                        );
                        return;
                    }

                    const centeredRect = this.centerAnnotation(
                        annotationClipboardData.bbox.width,
                        annotationClipboardData.bbox.height
                    );

                    if (!centeredRect) {
                        return;
                    }

                    const annotation = new ImgAnnotation();
                    annotation.imageId = this.selectedImage.id;
                    annotation.categoryId =
                        categoryOfAnnotationFromClipboard.id;
                    annotation.bbox = centeredRect;

                    this.annotationStateService.addAnnotation(annotation);

                    this.messageService.showTranslatedSuccessMsg(
                        'imgDb.imgLabeling.pasteAnnotationEvent.success',
                        {
                            name: categoryOfAnnotationFromClipboard.name,
                        },
                        this.TOAST_MESSAGE_DELAY
                    );
                });
        }
    }

    private centerAnnotation(
        annotationWidth: number,
        annotationHeight: number
    ) {
        const containerRect =
            this.panzoomParent.nativeElement.getBoundingClientRect();
        const imageRect =
            this.currentImage.nativeElement.getBoundingClientRect();
        const imgWidth = this.selectedImage.width;
        const imgHeight = this.selectedImage.height;

        // 1. Check if annotation width or height non-positive
        if (annotationWidth <= 0 || annotationHeight <= 0) {
            this.messageService.showTranslatedErrorMsg(
                'imgDb.imgLabeling.pasteAnnotationEvent.nonPositiveDimensions',
                null,
                this.TOAST_MESSAGE_DELAY
            );
            return null;
        }

        // 2. Check if annotation fits in image
        if (annotationWidth > imgWidth || annotationHeight > imgHeight) {
            this.messageService.showTranslatedErrorMsg(
                'imgDb.imgLabeling.pasteAnnotationEvent.tooLarge',
                null,
                this.TOAST_MESSAGE_DELAY
            );
            return null;
        }

        // --- X calculation ---
        let x: number;

        // If image is fully visible in container (image width <= container width)
        if (imageRect.width <= containerRect.width) {
            // Center annotation in image
            x = imgWidth / 2 - annotationWidth / 2;
        } else {
            // Image is larger than container, so only part is visible
            // Find visible image area in container (in screen coords)
            const visibleLeft = Math.max(containerRect.left, imageRect.left);
            const visibleRight = Math.min(containerRect.right, imageRect.right);

            // Find visible area in image coordinates
            const svg = this.labeling.nativeElement;

            const visibleLeftImg = this.getSVGPoint(
                svg,
                visibleLeft,
                imageRect.top
            ).x;
            const visibleRightImg = this.getSVGPoint(
                svg,
                visibleRight,
                imageRect.top
            ).x;

            // Center of visible area in image coords
            let centerX = (visibleLeftImg + visibleRightImg) / 2;

            // Try to center annotation in visible area
            x = centerX - annotationWidth / 2;

            // Clamp so annotation stays inside image
            if (x < 0) x = 0;
            if (x + annotationWidth > imgWidth) x = imgWidth - annotationWidth;
        }

        // --- Y calculation (same logic as X) ---
        let y: number;

        if (imageRect.height <= containerRect.height) {
            y = imgHeight / 2 - annotationHeight / 2;
        } else {
            const visibleTop = Math.max(containerRect.top, imageRect.top);
            const visibleBottom = Math.min(
                containerRect.bottom,
                imageRect.bottom
            );

            const svg = this.labeling.nativeElement;

            const visibleTopImg = this.getSVGPoint(
                svg,
                imageRect.left,
                visibleTop
            ).y;
            const visibleBottomImg = this.getSVGPoint(
                svg,
                imageRect.left,
                visibleBottom
            ).y;

            let centerY = (visibleTopImg + visibleBottomImg) / 2;

            y = centerY - annotationHeight / 2;

            if (y < 0) y = 0;
            if (y + annotationHeight > imgHeight)
                y = imgHeight - annotationHeight;
        }

        const newRect = new DOMRect(x, y, annotationWidth, annotationHeight);

        return this.offsetBoundingBoxIfOverlapping(newRect);
    }

    private offsetBoundingBoxIfOverlapping(rect: DOMRect) {
        const annotationWithSameStartPoint = this.annotations.filter(
            (annotation: ImgAnnotation) =>
                annotation.bbox.x === rect.x && annotation.bbox.y === rect.y
        );

        if (
            annotationWithSameStartPoint &&
            annotationWithSameStartPoint.length > 0
        ) {
            const newRect = new DOMRect(
                rect.x + 10,
                rect.y + 10,
                rect.width,
                rect.height
            );

            if (
                this.selectedImage.width >= newRect.x + newRect.width &&
                this.selectedImage.height >= newRect.y + newRect.height
            ) {
                return this.offsetBoundingBoxIfOverlapping(newRect);
            }
        }
        return rect;
    }

    private async handleCtrlCEvent(event: KeyboardEvent) {
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'c' || event.key === 'C')
        ) {
            let selectedAnnotation =
                this.annotationStateService.selectedAnnotation;

            if (!selectedAnnotation) {
                this.messageService.showTranslatedWarningMsg(
                    'imgDb.imgLabeling.copyAnnotationEvent.noSelectedAnnotation',
                    null,
                    this.TOAST_MESSAGE_DELAY
                );
                return;
            }

            const categoryOfSelectedAnnotation = this.categories.find(
                (category) => category.id === selectedAnnotation.categoryId
            );

            event.preventDefault();

            const annotationDataToCopy: AnnotationClipboardData = {
                categoryName: categoryOfSelectedAnnotation.name,
                bbox: {
                    width: selectedAnnotation.bbox.width,
                    height: selectedAnnotation.bbox.height,
                },
            };

            await this.keyEventsService
                .copyAnnotationToClipboard(annotationDataToCopy)
                .then(() => {
                    this.messageService.showTranslatedSuccessMsg(
                        'imgDb.imgLabeling.copyAnnotationEvent.success',
                        { name: annotationDataToCopy.categoryName },
                        this.TOAST_MESSAGE_DELAY
                    );
                });
        }
    }

    /**
     * Will add (if not exist) keydown and keyup event listeners with handlers
     * for key 'b'
     */
    private addKeydownAndUpBEvents() {
        if (!this.keydownAndUpBEventsExist && this.canEdit) {
            document.addEventListener('keydown', this.keydownBLambda);
            document.addEventListener('keyup', this.keyupBLambda);
            this.keydownAndUpBEventsExist = true;
        }
    }

    /**
     * Will remove (if exist) keydown and keyup event listeners with handlers
     * for key 'b'
     */
    private removeKeydownAndUpBEvents() {
        if (this.keydownAndUpBEventsExist) {
            document.removeEventListener('keydown', this.keydownBLambda);
            document.removeEventListener('keyup', this.keyupBLambda);
            this.keydownAndUpBEventsExist = false;
        }
    }

    private handleKeydownBEvent(event: KeyboardEvent) {
        if (event.key === 'b' || event.key === 'B') {
            event.preventDefault();
            if (
                !this.isMouseActionActive &&
                !this.keydownBAccepted &&
                this.isPanButtonActive &&
                this.isEditable()
            ) {
                this.keydownBAccepted = true;
                this.toggleImageDragging(false);
            }
        }
    }
    private handleKeyupBEvent(event: KeyboardEvent) {
        if (event.key === 'b' || event.key === 'B') {
            event.preventDefault();
            if (this.keydownBAccepted) {
                this.keydownBAccepted = false;
                if (this.isPanButtonClicked) {
                    this.toggleImageDragging(true);
                }
            }
        }
    }

    private addKeydownUndoRedoEvents() {
        if (!this.keydownUndoRedoEventsExist && this.canEdit) {
            document.addEventListener('keydown', this.keydownUndoRedoLambda);
            this.keydownUndoRedoEventsExist = true;
        }
    }

    private removeKeydownUndoRedoEvents() {
        if (this.keydownUndoRedoEventsExist) {
            document.removeEventListener('keydown', this.keydownUndoRedoLambda);
            this.keydownUndoRedoEventsExist = false;
        }
    }

    private handleKeydownUndoRedoEvents(event: KeyboardEvent) {
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'z' || event.key === 'Z')
        ) {
            event.preventDefault();
            if (this.annotationStateService.hasUndo() && this.canEdit) {
                this.annotationStateService.undo();
            }
        } else if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'y' || event.key === 'Y')
        ) {
            event.preventDefault();
            if (this.annotationStateService.hasRedo() && this.canEdit) {
                this.annotationStateService.redo();
            }
        }
    }

    /**
     * Handling HTML ResizeObserver Events,
     * like in div navigation expand/colapse
     */
    onResize(): void {
        this.updatePositions();
        this.keepImageInBounds();
    }

    updatePositions() {
        this.updateMoveButtonPosition();
        this.updateCornerPositions();
    }

    onImageLoad() {
        this.keepInBoundsTranslocationLimit = this.keepInBoundsTranslocationInitialLimit;;
        this.annotationStateService.selectedAnnotation = null;
        const imgElem = this.currentImage.nativeElement;

        const zoomRect: DOMRect =
            this.panzoomParent.nativeElement.getBoundingClientRect();
        const imgRect: DOMRect = imgElem.getBoundingClientRect();

        // scaling factor
        const scale = Math.log10(this.panZoomConfig.scalePerZoomLevel);
        // if already zoomed, factor for img width height values
        const zoomVal = Math.pow(
            this.panZoomConfig.scalePerZoomLevel,
            this.panZoomComponent.model().zoomLevel
        );
        // find zoomWidth
        const width =
            Math.log10(zoomRect.width / (imgRect.width / zoomVal)) / scale;
        // find zoomHeight
        const height =
            Math.log10(zoomRect.height / (imgRect.height / zoomVal)) / scale;

        // show image full, select minimum zoom
        let minZoom = Math.floor(Math.min(width, height));

        // cannot use ZoomToFit, since it will zoom image parts if image is larger than viewArea
        this.imageLoaded = true;
        this.onZoomChanged(minZoom, true);
        this.updateSvgStatus();
    }

    get isImageAndAnnotationsReady(): boolean {
        return this.imageLoaded && !this.annotationsLoading;
    }

    setSvg() {
        this.svgStatus = {
            w: '100%',
            h: '100%',
            viewBox: '0 0 0 0',
        };
    }

    ngOnDestroy(): void {
        // unsubscribe from the subscriptions
        this.annotationStateService
            .saveCommands(true, this.selectedImage?.id)
            .subscribe();
        this.alreadyDestroyed = true;
        this.destroy$.complete();
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
        this.annotationStateService.$annotations.next([]);
        clearInterval(this.saveIntervalId);
        document.body.removeEventListener('keydown', this.activateSpaceLambda);
        document.body.removeEventListener('keyup', this.deactivateSpaceLambda);
        document.body.removeAllListeners('mouseup');
        document.body.removeEventListener(
            'mouseleave',
            this.handlePostponedActionLambda
        );
        if (
            this.panzoomParent?.nativeElement &&
            this.panzoomParent.nativeElement instanceof Element
        ) {
            this.resizeObserver?.unobserve(this.panzoomParent.nativeElement);
        }
        this.annotationStateService.unselectImg();

        this.removeKeydownUndoRedoEvents();
        this.removeKeydownAndUpBEvents();
        this.removeCtrlCEvent();
        this.removeCtrlVEvent();
        this.removeSEvent();
    }

    // this is called every time the image is dragged or zoomed (for each animation step)
    onModelChanged(model: PanZoomModel) {
        this.updatePositions();

        if (!model || this.alreadyDestroyed) {
            return;
        }

        if (model.isPanning) {
            this.imagePanned = true;
            clearTimeout(this.zoomAlignmentTimeoutId); //abort alignment after zoom
            return; //do not update while panning, otherwise to many changeModel events
        } else {
            if (this.imagePanned) {
                this.imagePanned = false;
                this.keepImageInBounds();
            }

            //When panning do not update zoomService, to many updates, for "panning" time zoomService is out of sync
            let modelZoom = Math.round(model.zoomLevel);
            // go here if image is currently zooming
            clearTimeout(this.zoomAlignmentTimeoutId); //abort alignment after zoom

            if (
                !!this.wantedZoomLevel &&
                this.wantedZoomLevel === this.currentZoomLevel
            ) {
                //Quickfix helper, zoom changed after Load, external and by PanZoom.MouseWheel
                this.wantedZoomLevel = null;
            }

            if (
                modelZoom !== this.currentZoomLevel ||
                (this.wantedZoomLevel &&
                    this.wantedZoomLevel !== this.currentZoomLevel)
            ) {
                //Quickfix there is a cycle between PanModel update and zoomService
                this.zoomService.setCurrentZoomLevel(modelZoom);
                this.zoomLevelChanged.emit(modelZoom);
            }
            // align image after zoom animation is finished with delay
            this.zoomAlignmentTimeoutId = setTimeout(() => {
                this.keepImageInBounds();
            }, 50);
        }
    }

    onActivateBoundingBoxMovement(clickEvent: MouseEvent) {
        //set this so BE Annotation Update
        // does not interrupt moving (or updating in general)
        this.annotationStateService.bboxCurrentlyModifiable = true;

        clickEvent.preventDefault();

        const originalAnnotation =
            this.annotationStateService.deepCopyImgAnnotation(
                this.annotationStateService.selectedAnnotation
            );
        const movementStart = this.getSVGPoint(
            this.labeling.nativeElement,
            clickEvent.clientX,
            clickEvent.clientY
        );
        const rect = document.getElementById(
            this.annotationStateService.selectedAnnotation.id
        );
        const rectCursor = rect.style.cursor;
        rect.style.cursor = 'move';
        const initialRectPosition = {
            x: +rect.getAttribute('x'),
            y: +rect.getAttribute('y'),
        };
        const drawRect = (mouseMoveEvent: MouseEvent) => {
            const mouseSvg = this.getSVGPoint(
                this.labeling.nativeElement,
                mouseMoveEvent.clientX,
                mouseMoveEvent.clientY
            );
            const x = Math.max(
                0,
                Math.min(
                    initialRectPosition.x + mouseSvg.x - movementStart.x,
                    this.selectedImage.width - +rect.getAttribute('width')
                )
            );
            const y = Math.max(
                0,
                Math.min(
                    initialRectPosition.y + mouseSvg.y - movementStart.y,
                    this.selectedImage.height - +rect.getAttribute('height')
                )
            );
            this.annotationStateService.selectedAnnotation.bbox.x = x;
            this.annotationStateService.selectedAnnotation.bbox.y = y;
            this.updatePositions();
            this.updateSizeLabels(
                this.annotationStateService.selectedAnnotation
            );
        };

        const persistMove = () => {
            this.annotationStateService.bboxCurrentlyModifiable = false;
            rect.style.cursor = rectCursor;
            this.annotationStateService.updateAnnotation(
                this.annotationStateService.selectedAnnotation,
                originalAnnotation
            );
            document.body.removeEventListener('mousemove', drawRect);
            document.body.removeEventListener('mouseup', persistMove);
        };
        document.body.addEventListener('mousemove', drawRect);
        document.body.addEventListener('mouseup', persistMove);
    }

    public getMoveButtonPosition() {
        const activeAnnotation = this.annotationStateService.selectedAnnotation;
        let x = 0;
        let y = 0;
        if (activeAnnotation && this.labeling) {
            const annotationViewportCoordinate =
                this.svgCoordinatesToViewPortCoordinates(
                    this.labeling.nativeElement,
                    activeAnnotation.bbox.x,
                    activeAnnotation.bbox.y - (this.boundingBoxTitleHeight + 4)
                );
            const annotationRightBottomCorner =
                this.svgCoordinatesToViewPortCoordinates(
                    this.labeling.nativeElement,
                    activeAnnotation.bbox.x + activeAnnotation.bbox.width,
                    activeAnnotation.bbox.y + activeAnnotation.bbox.height + 4
                );
            const annotationWidth =
                annotationRightBottomCorner.x - annotationViewportCoordinate.x;

            const imageArea =
                this.panzoomParent.nativeElement.getBoundingClientRect();
            x = annotationViewportCoordinate.x;
            y = annotationViewportCoordinate.y - this.moveButtonHeight;
            if (x < imageArea.x || x > imageArea.x + imageArea.width) {
                x = x + annotationWidth;
                y = this.svgCoordinatesToViewPortCoordinates(
                    this.labeling.nativeElement,
                    0,
                    activeAnnotation.bbox.y
                ).y;
                if (x > imageArea.x + imageArea.width || x < imageArea.x) {
                    x = -50;
                }
            }
            if (y < imageArea.y || y > imageArea.y + imageArea.height) {
                y = annotationRightBottomCorner.y;
                if (y > imageArea.y + imageArea.height || y < imageArea.y) {
                    y = -50;
                }
            }
        }
        return { x: x, y: y };
    }

    public keepPositionInBounds(x: number, y: number): [number, number] {
        if (!this.panzoomParent) {
            return null;
        }
        const imageArea =
            this.panzoomParent.nativeElement.getBoundingClientRect();
        let inBoundsX = x;
        let inBoundsY = y;

        if (x < imageArea.x || x > imageArea.x + imageArea.width) {
            inBoundsX = -50;
        }
        if (y < imageArea.y || y > imageArea.y + imageArea.height) {
            inBoundsY = -50;
        }
        return [inBoundsX, inBoundsY];
    }

    public updateCornerPositions() {
        if (!this.canEdit) {
            return;
        }
        const activeAnnotation = this.annotationStateService.selectedAnnotation;
        if (activeAnnotation && this.labeling) {
            const annotationViewportCoordinate =
                this.svgCoordinatesToViewPortCoordinates(
                    this.labeling.nativeElement,
                    activeAnnotation.bbox.x,
                    activeAnnotation.bbox.y
                );
            const annotationRightBottomCorner =
                this.svgCoordinatesToViewPortCoordinates(
                    this.labeling.nativeElement,
                    activeAnnotation.bbox.x + activeAnnotation.bbox.width,
                    activeAnnotation.bbox.y + activeAnnotation.bbox.height
                );
            const annotationWidth =
                annotationRightBottomCorner.x - annotationViewportCoordinate.x;
            const annotationHeight =
                annotationRightBottomCorner.y - annotationViewportCoordinate.y;

            this.cornerClasses.forEach((cornerClass) => {
                const cornerDOM = document.getElementsByClassName(
                    `corner ${cornerClass}`
                )[0] as HTMLElement;
                let cornerX: number = 0;
                let cornerY: number = 0;

                if (cornerClass === 'top-left') {
                    cornerX = annotationViewportCoordinate.x;
                    cornerY = annotationViewportCoordinate.y;
                } else if (cornerClass === 'top-right') {
                    cornerX = annotationViewportCoordinate.x + annotationWidth;
                    cornerY = annotationViewportCoordinate.y;
                } else if (cornerClass === 'bottom-left') {
                    cornerX = annotationViewportCoordinate.x;
                    cornerY = annotationViewportCoordinate.y + annotationHeight;
                } else if (cornerClass === 'bottom-right') {
                    cornerX = annotationViewportCoordinate.x + annotationWidth;
                    cornerY = annotationViewportCoordinate.y + annotationHeight;
                }

                cornerX -= this.cornerSize / 2;
                cornerY -= this.cornerSize / 2;

                [cornerX, cornerY] = this.keepPositionInBounds(
                    cornerX,
                    cornerY
                );

                const visible = cornerX >= 0 || cornerY >= 0;

                // need to set attributes here and not in the template, as update of template is too slow
                cornerDOM.setAttribute(
                    'style',
                    `left: ${cornerX}px; top: ${cornerY}px; width: ${
                        this.cornerSize
                    }px; height: ${this.cornerSize}px; ${
                        visible ? '' : `visibility: hidden;`
                    }`
                );
            });
        } else {
            this.cornerClasses.forEach((cornerClass) => {
                const cornerDOM = document.getElementsByClassName(
                    `corner ${cornerClass}`
                )[0] as HTMLElement;
                cornerDOM?.setAttribute('style', `visibility: hidden;`);
            });
        }
    }

    private updateMoveButtonPosition() {
        const moveButtonPosition = this.getMoveButtonPosition();
        const boundingBoxMoveButton = document.getElementById(
            'move-bounding-box-button'
        );
        if (moveButtonPosition && boundingBoxMoveButton) {
            boundingBoxMoveButton.style.left = `${moveButtonPosition.x}px`;
            boundingBoxMoveButton.style.top = `${moveButtonPosition.y}px`;
        }
    }

    private keepImageInBounds() {
        if (
            this.alreadyDestroyed ||
            !this.panZoomComponent ||
            !this.currentImage ||
            !this.panzoomParent ||
            !!this.panDeltaTimeout
        ) {
            return; // some DOM not fetched or in process, skip call
        }

        const model = this.panZoomComponent.model();
        const parentWidth = this.panzoomParent.nativeElement.offsetWidth;
        const parentHeight = this.panzoomParent.nativeElement.offsetHeight;
        const imgBounding =
            this.currentImage.nativeElement.getBoundingClientRect();
        const imgWidth = imgBounding.width;
        const imgHeight = imgBounding.height;

        // current zoomLevel. This will return 0,1,2,etc, NOT the actual scale like 1, 1.2, 1.44 and so on
        const zoomLevel = model.zoomLevel;

        // actual scale value
        const scaleValue = Math.pow(
            this.panZoomConfig.scalePerZoomLevel,
            zoomLevel
        );

        const newX = this.calculateValue(imgWidth, parentWidth, model.pan.x);
        const newY = this.calculateValue(imgHeight, parentHeight, model.pan.y);

        // set the delta position of the image, i.e. the distance of x and y that the image needs to be panned by
        const dx = ((newX - model.pan.x) * -1) / scaleValue;
        const dy = ((newY - model.pan.y) * -1) / scaleValue;

        // We need to check if there are any differences and only call penDelta when necessary.
        // Calling penDelta all the time can disable zoom.
        // We're comparing the absolute values to 2 and not 0 in order to avoid jittering due to small margins like 0.1, 0.2, etc
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
            this.panDeltaTimeout = setTimeout(() => {
                this.panZoomComponent.panDelta(
                    {
                        x: Math.min(
                            Math.max(dx, -this.keepInBoundsTranslocationLimit),
                            this.keepInBoundsTranslocationLimit
                        ),
                        y: Math.min(
                            Math.max(dy, -this.keepInBoundsTranslocationLimit),
                            this.keepInBoundsTranslocationLimit
                        ),
                    },
                    0.01
                );
                this.panDeltaTimeout = null;
            }, 0);
            this.keepInBoundsTranslocationLimit =
                this.keepInBoundsTranslocationLimit * 0.7;
        } else {
            this.keepInBoundsTranslocationLimit = this.keepInBoundsTranslocationInitialLimit;;
        }
    }

    private calculateValue(imgValue: any, parentValue: any, panValue: number) {
        let newValue: number = panValue;
        // if the width of the image is less than or equal to the frame width
        // we need to center the image horizontally
        if (imgValue <= parentValue) {
            newValue = parentValue / 2 - imgValue / 2;
        } else {
            // if the image width is larger than the frame, we need to make sure that edges
            // of the image don't go inside the frame, in other words, the image should always
            // cover the entire width of the frame.
            // x > 0 means the left side of the image inside the frame
            if (!panValue || panValue > 0) {
                newValue = 0; // return the left side of the image on the left edge of the frame, i.e. first valid value
            } else if (panValue + imgValue < parentValue) {
                // check the right side of them image, x + imgWidth < parentWidth means the right side of the image is inside the frame.
                // return it to the position where right side of the image is on the right edge of the frame, i.e. first valid value.
                // x + imgWidth = parentWidth, i.e. x = parentWidth - imgWidth
                newValue = parentValue - imgValue;
            }
        }
        return newValue;
    }

    private onZoomChanged(
        zoomLevel: number,
        updateGlobalZoom: boolean = false
    ) {
        if (
            zoomLevel > this.panZoomConfig.zoomLevels ||
            zoomLevel < ImgDbZoomService.MIN_IMAGE_ZOOM ||
            !this.selectedImage ||
            !this.panZoomComponent ||
            this.panZoomComponent.model().zoomLevel === zoomLevel
        ) {
            return;
        }

        this.panZoomComponent?.changeZoomLevel(zoomLevel, null);

        this.wantedZoomLevel = zoomLevel;
        if (updateGlobalZoom) {
            if (this.zoomService.currentZoomLevelLabelDialog !== zoomLevel) {
                this.zoomService.zoomLevelLabelDialog.next(zoomLevel);
            }
        }
    }

    onPanButtonClick() {
        this.isPanButtonActive = !this.isPanButtonActive;
        this.isPanButtonClicked = this.isPanButtonActive;
        this.toggleImageDragging(this.isPanButtonActive);
    }

    onRulerButtonClick() {
        this.isRulerButtonActive = !this.isRulerButtonActive;
        this.showSizeLabels = this.isRulerButtonActive;
    }

    resetSizeLabelCache() {
        this.sizeLabelCache = {};
        this.initializedSizeLabels = {};
    }

    getHeightLabelData(
        annotation: ImgAnnotation,
        heightLabel: SVGTextElement
    ): object {
        const key = annotation.id + '_h';
        const cache = this.sizeLabelCache[key];
        if (cache !== null) {
            return cache;
        }
        return this.updateHeightLabel(annotation, heightLabel);
    }

    getWidthLabelData(
        annotation: ImgAnnotation,
        widthLabel: SVGTextElement
    ): object {
        const key = annotation.id + '_w';
        const cache = this.sizeLabelCache[key];
        if (cache !== null) {
            return cache;
        }
        return this.updateWidthLabel(annotation, widthLabel);
    }

    updateSizeLabels(annotation: ImgAnnotation): void {
        this.updateWidthLabel(annotation);
        this.updateHeightLabel(annotation);
    }

    getUninitializedSizeLabel(): object {
        return {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            text: {
                text: '',
                transform: '',
                x: 0,
                y: 0,
            },
        };
    }

    updateWidthLabel(
        annotation: ImgAnnotation,
        widthLabel: SVGTextElement = null
    ): object {
        const widthLabelKey = annotation.id + '_w';

        if (widthLabel === null) {
            widthLabel = document.getElementById(
                widthLabelKey
            ) as unknown as SVGTextElement;
        }
        if (widthLabel === null) {
            this.sizeLabelCache[widthLabelKey] = null;
            return this.getUninitializedSizeLabel();
        }

        const bbox = {
            x: annotation.bbox.x,
            y: annotation.bbox.y,
            width: Math.round(annotation.bbox.width * 10) / 10,
            height: Math.round(annotation.bbox.height * 10) / 10,
        };

        const fontSize = parseInt(window.getComputedStyle(widthLabel).fontSize);
        const widthLabelText = Math.round(bbox.width) + ' px';
        widthLabel.textContent = widthLabelText;
        const widthLabelTextLength = widthLabel.getComputedTextLength();
        const widthLabelHeight = this.boundingBoxTitleHeight; // match the title box
        const widthLabelWidth = widthLabelTextLength + fontSize;
        const widthLabelX = bbox.x + bbox.width / 2 - widthLabelWidth / 2;
        let widthLabelY = bbox.y + bbox.height;
        const widthLabelLowerCornersY = widthLabelY + widthLabelHeight;
        if (widthLabelLowerCornersY > this.selectedImage.height) {
            // should we leave the image to the bottom, move the label to the inside of the annotation
            widthLabelY -= widthLabelHeight;
        }
        const widthLabelTextY =
            widthLabelY +
            widthLabelHeight / 2 +
            (widthLabelHeight - fontSize) / 2;
        const widthLabelTextX =
            widthLabelX + widthLabelWidth / 2 - widthLabelTextLength / 2;

        const widthLabelValues = {
            x: widthLabelX,
            y: widthLabelY,
            width: `${widthLabelWidth}`,
            height: `${widthLabelHeight}`,
            text: {
                text: widthLabelText,
                x: widthLabelTextX,
                y: widthLabelTextY,
                transform: '',
            },
        };
        if (widthLabelTextLength > 0) {
            this.sizeLabelCache[widthLabelKey] = widthLabelValues;
        }

        return widthLabelValues;
    }

    updateHeightLabel(
        annotation: ImgAnnotation,
        heightLabel: SVGTextElement = null
    ): object {
        const heightLabelKey = annotation.id + '_h';

        if (heightLabel === null) {
            heightLabel = document.getElementById(
                heightLabelKey
            ) as unknown as SVGTextElement;
        }
        if (heightLabel === null) {
            this.sizeLabelCache[heightLabelKey] = null;
            return this.getUninitializedSizeLabel();
        }

        const bbox = {
            x: annotation.bbox.x,
            y: annotation.bbox.y,
            width: Math.round(annotation.bbox.width * 10) / 10,
            height: Math.round(annotation.bbox.height * 10) / 10,
        };
        const fontSize = parseInt(
            window.getComputedStyle(heightLabel).fontSize
        );

        const heightLabelText = Math.round(bbox.height) + ' px';
        heightLabel.textContent = heightLabelText;
        let heightLabelTextLength = heightLabel.getComputedTextLength();
        const heightLabelHeight = heightLabelTextLength + fontSize;
        const heightLabelWidth = this.boundingBoxTitleHeight; // match the title box
        let heightLabelX = bbox.x - heightLabelWidth;
        if (heightLabelX < 0) {
            // should we leave the image to the left, move the label to the inside of the annotation
            heightLabelX += heightLabelWidth;
        }
        const heightLabelY = bbox.y + bbox.height / 2 - heightLabelHeight / 2;
        const heightLabelTextX =
            heightLabelX +
            heightLabelWidth / 2 +
            (heightLabelWidth - fontSize) / 2;
        const heightLabelTextY =
            heightLabelY + heightLabelHeight / 2 + heightLabelTextLength / 2;
        const transform =
            'rotate(-90 ' + heightLabelTextX + ',' + heightLabelTextY + ')';
        const heightLabelValues = {
            x: heightLabelX,
            y: heightLabelY,
            height: `${heightLabelHeight}`,
            width: `${heightLabelWidth}`,
            text: {
                text: heightLabelText,
                transform: transform,
                x: heightLabelTextX,
                y: heightLabelTextY,
            },
        };
        if (heightLabelTextLength > 0) {
            this.sizeLabelCache[heightLabelKey] = heightLabelValues;
        }

        return heightLabelValues;
    }

    onActivateSpaceDragging(event: KeyboardEvent) {
        if (event.key === ' ' && this.panZoomComponent?.model().zoomLevel > 0) {
            event.preventDefault();
            if (this.isMouseActionActive) {
                this.spaceActionTriggered = 'on';
            } else {
                this.toggleImageDragging(true);
            }
        }
    }

    onDeactivateSpaceDragging(event: KeyboardEvent) {
        if (event.key === ' ') {
            this.spaceActionTriggered = null;
            if (!this.isPanButtonClicked) {
                if (this.isMouseActionActive) {
                    this.spaceActionTriggered = 'off';
                } else {
                    this.toggleImageDragging(false);
                }
            }
        }
    }

    onHandleMouseAction() {
        this.isMouseActionActive = true;
        this.cursorView = this.isDraggingActive
            ? 'grabbing'
            : this.decideCursorCrosshairOrDefault();
    }

    private decideCursorCrosshairOrDefault() {
        if (
            this.canEdit &&
            (this.drawingEventListenerRef || this.selectedCategory)
        ) {
            return 'crosshair';
        }
        return 'default';
    }

    handlePostponedAction() {
        this.isMouseActionActive = false;
        if (this.spaceActionTriggered) {
            switch (this.spaceActionTriggered) {
                case 'on':
                    this.toggleImageDragging(true);
                    break;
                case 'off':
                    this.toggleImageDragging(false);
                    break;
            }
            this.spaceActionTriggered = null;
        }
    }

    // Disables and enables dragging the image
    toggleImageDragging(enable: boolean) {
        if (this.panZoomComponent) {
            this.zoomConfigPanOnClickDrag.set(enable);
            this.cursorView = enable
                ? 'grab'
                : this.decideCursorCrosshairOrDefault();
            this.isDraggingActive = enable;
            this.isPanButtonActive = enable;
        }
        this.enableDrawing(!enable);
    }

    updateSvgStatus() {
        this.svgStatus = {
            w: this.currentImage.nativeElement.clientWidth,
            h: this.currentImage.nativeElement.clientHeight,
            viewBox: `0 0 ${this.selectedImage.width} ${this.selectedImage.height}`,
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            if (changes.selectedCategory && this.labeling) {
                this.enableDrawing(this.isEditable());
                this.toggleImageDragging(false);
                this.isPanButtonClicked = false;
            } else if (
                changes.splitScreenState &&
                this.imageLoaded &&
                this.panzoomParent &&
                this.currentImage
            ) {
                this.onImageLoad();
                this.keepImageInBounds();
            } else if (changes.canEdit) {
                if (this.canEdit) {
                    this.addKeydownUndoRedoEvents();
                    this.addKeydownAndUpBEvents();
                    this.addCtrlVEvent();
                } else {
                    this.removeKeydownUndoRedoEvents();
                    this.removeKeydownAndUpBEvents();
                    this.removeCtrlVEvent();
                }
            }
        }
    }

    ngAfterViewChecked(): void {
        this.serializedImgRefs = this.queryList.map((ref) => ref.nativeElement);
        this.queryList.changes.subscribe((_) => {
            this.serializedImgRefs = this.queryList.map(
                (ref) => ref.nativeElement
            );
        });
    }

    getSvgViewbox() {
        if (!this.selectedImage) return;

        return `0 0 ${this.selectedImage.width} ${this.selectedImage.height}`;
    }

    enableDrawing(enable: boolean): void {
        const svg = this.labeling.nativeElement;

        if (enable && !this.drawingEventListenerRef && this.isEditable()) {
            this.drawingEventListenerRef = (evt) => {
                this.handleDrawing(evt);
            };
            svg.addEventListener('mousedown', this.drawingEventListenerRef);
        } else if (!enable) {
            svg.removeEventListener('mousedown', this.drawingEventListenerRef);
            this.drawingEventListenerRef = null;
        }
    }

    handleDrawing(event: MouseEvent) {
        const svg = this.labeling.nativeElement;
        const start = this.getSVGPoint(svg, event.clientX, event.clientY);
        const rect = this.createSVGElement('rect');
        const g = this.createSVGElement('g');
        svg.appendChild(g);
        g.appendChild(rect);

        const drawRect = (e: MouseEvent) => {
            const p = this.getSVGPoint(svg, e.clientX, e.clientY);
            const [x, y, w, h] = this.calculateRect(start, p);
            rect.setAttribute('x', x.toString());
            rect.setAttribute('y', y.toString());
            rect.setAttribute('width', w.toString());
            rect.setAttribute('height', h.toString());
            rect.setAttribute(
                'stroke',
                this.getColor(this.selectedCategory.name)
            );
            rect.setAttribute('fill', 'transparent');
        };

        const endDraw = (_) => {
            const rectWidth = parseFloat(rect.getAttribute('width'));
            const rectHeight: number = parseFloat(rect.getAttribute('height'));
            const rectX = parseFloat(rect.getAttribute('x'));
            const rectY = parseFloat(rect.getAttribute('y'));
            if (rectWidth > 0 && rectHeight > 0) {
                let annotation = new ImgAnnotation();
                annotation.imageId = this.selectedImage.id;
                annotation.categoryId = this.selectedCategory.id + '';
                annotation.bbox = new DOMRect(
                    rectX,
                    rectY,
                    rectWidth,
                    rectHeight
                );
                this.annotationStateService.addAnnotation(annotation);
                g.remove();
            }

            svg.removeEventListener('mousemove', drawRect);
            document.body.removeEventListener('mouseup', endDraw);
        };

        svg.addEventListener('mousemove', drawRect);
        document.body.addEventListener('mouseup', endDraw);
    }

    selectAnnotation(annotation: ImgAnnotation) {
        if (!this.zoomConfigPanOnClickDrag()) {
            this.annotationStateService.selectAnnotation(annotation.id);
            this.updatePositions();
        }
    }

    getSVGPoint(svg: SVGSVGElement, x: number, y: number): SVGPoint {
        const p = svg.createSVGPoint();
        p.x = x;
        p.y = y;
        return p.matrixTransform(svg.getScreenCTM().inverse());
    }

    svgCoordinatesToViewPortCoordinates(
        svg: SVGSVGElement,
        x: number,
        y: number
    ): SVGPoint {
        const p = svg.createSVGPoint();
        p.x = x;
        p.y = y;
        return p.matrixTransform(svg.getScreenCTM());
    }

    createSVGElement(tagName: string): SVGElement {
        return document.createElementNS('http://www.w3.org/2000/svg', tagName);
    }

    calculateRect(
        start: DOMPoint,
        end: DOMPoint
    ): [number, number, number, number] {
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const w = Math.abs(end.x - start.x);
        const h = Math.abs(end.y - start.y);
        return [x, y, w, h];
    }

    getImgCategoryName(id: string) {
        return this.findImgCategoryById(id)?.name;
    }

    private findImgCategoryById(id: string) {
        return this.categories.find((category: ImgCategory) => {
            return category.id === id;
        });
    }

    isEditable(): boolean {
        return (
            this.selectedCategory !== null &&
            this.selectedCategory !== undefined &&
            this.canEdit
        );
    }

    onImagesChanged(): void {
        if (!this.labeling || !this.labeling.nativeElement) {
            return;
        }
        const rects = this.labeling.nativeElement.querySelectorAll('g');
        if (!!rects && rects.length) {
            rects.forEach((element) => {
                element.remove();
            });
        }
    }

    onHandleBoundingBoxResize(clickEvent: MouseEvent): void {
        //set this so BE Annotation Update
        // does not interrupt moving (or updating in general)
        this.annotationStateService.bboxCurrentlyModifiable = true;

        clickEvent.preventDefault();

        const target: HTMLElement = clickEvent.target as HTMLElement;
        const classList: DOMTokenList = target.classList;
        const corner = {
            topLeftCorner: classList.contains('top-left'),
            topRightCorner: classList.contains('top-right'),
            bottomLeftCorner: classList.contains('bottom-left'),
            bottomRightCorner: classList.contains('bottom-right'),
        };

        const originalAnnotation =
            this.annotationStateService.deepCopyImgAnnotation(
                this.annotationStateService.selectedAnnotation
            );
        const movementStart = this.getSVGPoint(
            this.labeling.nativeElement,
            clickEvent.clientX,
            clickEvent.clientY
        );
        const rect = document.getElementById(
            this.annotationStateService.selectedAnnotation.id
        );

        const initialRectPosition = {
            x: +rect.getAttribute('x'),
            y: +rect.getAttribute('y'),
        };

        const initialRectSize = {
            width: +rect.getAttribute('width'),
            height: +rect.getAttribute('height'),
        };

        const initialRectCornerPositions = {
            topLeft: {
                x: initialRectPosition.x,
                y: initialRectPosition.y,
            },
            topRight: {
                x: initialRectPosition.x + initialRectSize.width,
                y: initialRectPosition.y,
            },
            bottomLeft: {
                x: initialRectPosition.x,
                y: initialRectPosition.y + initialRectSize.height,
            },
            bottomRight: {
                x: initialRectPosition.x + initialRectSize.width,
                y: initialRectPosition.y + initialRectSize.height,
            },
        };

        const updateOnResizeCallback = (event) =>
            this.updateOnResize(
                event,
                movementStart,
                corner,
                initialRectCornerPositions
            );
        const persistResizeCallback = () =>
            this.persistResize(updateOnResizeCallback, originalAnnotation);
        document.body.addEventListener('mousemove', updateOnResizeCallback);
        document.body.addEventListener('mouseup', persistResizeCallback, {
            once: true,
        });
    }

    updateOnResize(
        mouseMoveEvent: MouseEvent,
        movementStart,
        corner,
        initialRectCornerPositions
    ): void {
        const mouseSvg = this.getSVGPoint(
            this.labeling.nativeElement,
            mouseMoveEvent.clientX,
            mouseMoveEvent.clientY
        );

        if (corner.topLeftCorner) {
            const x = Math.max(
                // check that we are not outside to the left of the image
                0,
                Math.min(
                    // check that we are not right of the corners on the oppsite side
                    initialRectCornerPositions.topLeft.x +
                        mouseSvg.x -
                        movementStart.x,
                    initialRectCornerPositions.topRight.x
                )
            );
            const y = Math.max(
                // check that we are not outside to the top of the image
                0,
                Math.min(
                    // check that we are not below the opposite corners
                    initialRectCornerPositions.topLeft.y +
                        mouseSvg.y -
                        movementStart.y,
                    initialRectCornerPositions.bottomLeft.y
                )
            );
            const width = initialRectCornerPositions.bottomRight.x - x;
            const height = initialRectCornerPositions.bottomRight.y - y;
            this.annotationStateService.selectedAnnotation.bbox.x = x;
            this.annotationStateService.selectedAnnotation.bbox.y = y;
            this.annotationStateService.selectedAnnotation.bbox.width = width;
            this.annotationStateService.selectedAnnotation.bbox.height = height;
        } else if (corner.topRightCorner) {
            // check that we are not outside to the right of the image
            const x = Math.min(
                this.selectedImage.width,
                Math.max(
                    // check that we are not outside to the left of the image
                    0,
                    Math.max(
                        // check that we are not left of the corners on the oppsite side
                        initialRectCornerPositions.topRight.x +
                            mouseSvg.x -
                            movementStart.x,
                        initialRectCornerPositions.bottomLeft.x
                    )
                )
            );
            const y = Math.max(
                // check that we are not outside to the top of the image
                0,
                Math.min(
                    // check that we are not below the opposite corners
                    initialRectCornerPositions.topRight.y +
                        mouseSvg.y -
                        movementStart.y,
                    initialRectCornerPositions.bottomLeft.y
                )
            );
            const width = x - initialRectCornerPositions.bottomLeft.x;
            const height = initialRectCornerPositions.bottomLeft.y - y;
            this.annotationStateService.selectedAnnotation.bbox.y = y;
            this.annotationStateService.selectedAnnotation.bbox.width = width;
            this.annotationStateService.selectedAnnotation.bbox.height = height;
        } else if (corner.bottomLeftCorner) {
            const x = Math.max(
                // check that we are not outside to the left of the image
                0,
                Math.min(
                    // check that we are not right of the corners on the oppsite side
                    initialRectCornerPositions.bottomLeft.x +
                        mouseSvg.x -
                        movementStart.x,
                    initialRectCornerPositions.topRight.x
                )
            );
            const y = Math.min(
                // check that we are not outside to the bottom of the image
                this.selectedImage.height,
                Math.max(
                    // check that we are not above the opposite corners
                    initialRectCornerPositions.bottomLeft.y +
                        mouseSvg.y -
                        movementStart.y,
                    initialRectCornerPositions.topRight.y
                )
            );
            const width = initialRectCornerPositions.topRight.x - x;
            const height = y - initialRectCornerPositions.topRight.y;
            this.annotationStateService.selectedAnnotation.bbox.x = x;
            this.annotationStateService.selectedAnnotation.bbox.width = width;
            this.annotationStateService.selectedAnnotation.bbox.height = height;
        } else if (corner.bottomRightCorner) {
            const x = Math.min(
                // check that we are not outside to the right of the image
                this.selectedImage.width,
                Math.max(
                    // check that we are not outside to the left of the image
                    0,
                    Math.max(
                        // check that we are not left of the corners on the oppsite side
                        initialRectCornerPositions.bottomRight.x +
                            mouseSvg.x -
                            movementStart.x,
                        initialRectCornerPositions.topLeft.x
                    )
                )
            );
            const y = Math.min(
                // check that we are not outside to the bottom of the image
                this.selectedImage.height,
                Math.max(
                    // check that we are not outside to the top of the image
                    0,
                    Math.max(
                        // check that we are not above the opposite corners
                        initialRectCornerPositions.bottomRight.y +
                            mouseSvg.y -
                            movementStart.y,
                        initialRectCornerPositions.topRight.y
                    )
                )
            );
            const width = x - initialRectCornerPositions.topLeft.x;
            const height = y - initialRectCornerPositions.topLeft.y;
            this.annotationStateService.selectedAnnotation.bbox.width = width;
            this.annotationStateService.selectedAnnotation.bbox.height = height;
        } else {
            this.annotationStateService.bboxCurrentlyModifiable = false;
            return;
        }

        this.updatePositions();
        this.updateSizeLabels(this.annotationStateService.selectedAnnotation);
    }

    persistResize = (
        updateOnResizeCallback,
        originalAnnotation: ImgAnnotation
    ): void => {
        this.annotationStateService.bboxCurrentlyModifiable = false;
        this.annotationStateService.updateAnnotation(
            this.annotationStateService.selectedAnnotation,
            originalAnnotation
        );
        document.body.removeEventListener('mousemove', updateOnResizeCallback);
    };

    getColor(categoryName: string) {
        return this.colorService.getColor(categoryName);
    }

    private sizeLabelsNeedUpdate(annotation: ImgAnnotation): boolean {
        const initializedSizeLabelBbox =
            this.initializedSizeLabels[annotation.id];
        if (!!initializedSizeLabelBbox) {
            if (
                annotation.bbox.x === initializedSizeLabelBbox.x &&
                annotation.bbox.y === initializedSizeLabelBbox.y &&
                annotation.bbox.width === initializedSizeLabelBbox.width &&
                annotation.bbox.height === initializedSizeLabelBbox.height
            ) {
                return false;
            }
        }
        return true;
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event) {
        if (
            this.annotationStateService?.changedAnnotationState() &&
            this.selectedImage?.id
        ) {
            this.annotationStateService
                .saveCommands(false, this.selectedImage.id)
                .subscribe();
            event.preventDefault();
            event.returnValue = false;
        }
        this.alreadyDestroyed = true;
    }
}
